import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    const cat = categories.find(c => c.slug === activeSlug);
    document.title = `${cat?.name || '分类'}漫画 - 香香漫画韩漫免费阅读`;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const currentCategory = categories.find(c => c.slug === activeSlug);
  const filteredComics = comics.filter(c => c.categorySlug === activeSlug);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'}漫画 - 香香漫画`,
    description: `香香漫画${currentCategory?.name || ''}漫画专区，免费阅读海量${currentCategory?.name || ''}韩漫全集，下拉式畅读体验`,
    url: `https://xiangxiangmanhua.com/category/${activeSlug}`,
    hasPart: filteredComics.map(c => ({
      '@type': 'CreativeWork',
      name: c.title,
      url: `https://xiangxiangmanhua.com/comic/${c.id}`,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiangxiangmanhua.com/' },
      { '@type': 'ListItem', position: 2, name: `${currentCategory?.name || '分类'}漫画` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <nav className="px-4 md:px-6 py-4" aria-label="面包屑导航">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-800">{currentCategory?.name || '分类'}</li>
            </ol>
          </nav>

          <section className="px-4 md:px-6 pb-4">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
              {currentCategory?.name || '分类'}漫画
            </h1>
            <p className="text-sm text-foreground-500">
              共 {filteredComics.length} 部{currentCategory?.name}韩漫，下拉式免费阅读
            </p>
          </section>

          <div className="px-4 md:px-6 pb-6">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="漫画分类">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  role="tab"
                  aria-selected={activeSlug === cat.slug}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <section className="px-4 md:px-6 pb-14">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 香香漫画${comic.category}分类`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-md text-background-50 text-xs font-medium ${
                        comic.status === '连载中' ? 'bg-accent-500' : 'bg-secondary-500'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm text-foreground-800 font-medium group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author} · {comic.rating}分</p>
                </Link>
              ))}
            </div>
            {filteredComics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请选择其他分类</p>
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}